/**
 * Raising a QUOTE or an INVOICE from a deal, and the one refusal that routes
 * somewhere else.
 *
 * `POST /deals/{id}/convert` (→ quote) and `POST /deals/{id}/invoices`
 * (→ invoice) are the only two calls that carry a deal's LINE ITEMS across; a
 * create form seeded from the deal cannot, because a line is
 * `POST /{resource}/{id}/products` and cannot exist before the record does.
 *
 * Both are **repeatable — there is no 409**. Since 2026-10-18 **both move the
 * deal's stage forward** (quote → the quote-sent stage, invoice → the won
 * stage; never backwards), but neither writes the deal's lines. This module
 * holds the sentences that say so and the model for the
 * refusal, so the deal page, the two conversion forms and the invoice a person
 * lands on cannot word the same fact three ways.
 *
 * ⚠️ **BOTH ROUTES NOW TAKE THE SAME OPTIONAL `lines` OVERRIDE**, keyed by
 * `dealLineId` — see `@/lib/deal-convert-lines`, which is one module for both
 * bodies rather than two identical ones. Neither override touches the DEAL: the
 * edits apply to the new document only.
 */
import { todayIsoDate } from '@/lib/invoice-utils';
import { INVOICE_PAYMENT_TERM_DAYS, type InvoiceHeaderPrefill } from '@/lib/quote-to-invoice';
import { addDaysIso } from '@/lib/quote-utils';
import type { Deal } from '@/lib/types';
import { ApiError } from './api';

/**
 * `POST /deals/{id}/invoices` refused because the deal has lines and the
 * **company** has no `xeroSalesTaxRate`.
 *
 * ⚠️ **INVOICE ONLY, and the asymmetry is the decision rather than an
 * oversight.** `POST /deals/{id}/convert` is deliberately not subject to it: a
 * quote is an *offer*, read and priced before it reaches anybody, and refusing
 * there would block quoting every one of the 148 companies imported from Xero —
 * all of which predate migration 092 with the rate null. An invoice is the
 * document that demands money. Do not "harmonise" the two paths.
 *
 * ⚠️ **It is not an `XERO_*` code, and must not be routed to `/integrations`.**
 * It fires whether or not Xero is connected, whether or not the company is
 * linked, and whether or not the invoice is ever pushed. The rate is the
 * percentage that becomes `f_invoice_products.tax_rate` — the figure EMERGE
 * itself prints — so this is a gap in our own record, not in an integration.
 *
 * ⚠️ **It keys on the RATE, never on `xeroSalesTaxType`.** The type is Xero's
 * code and yields no percentage at all: `ZERORATEDOUTPUT`, `NONE` and
 * `EXEMPTOUTPUT` are all 0% in this tenant. A rate of `0.00` is a zero-rated
 * customer somebody chose and converts happily; `null` is nobody having
 * answered.
 *
 * ⚠️ **Nothing is written when it fires.** The check runs before the
 * transaction opens, so a refused call leaves no invoice behind — which matters
 * here precisely because the endpoint is repeatable with no 409, and a late
 * refusal would leave residue on every firing.
 */
export const COMPANY_HAS_NO_TAX_RATE_CODE = 'COMPANY_HAS_NO_TAX_RATE';

/**
 * The two sentences the server sends with {@link COMPANY_HAS_NO_TAX_RATE_CODE},
 * kept apart because **both have to render**.
 *
 * `message` names the company and states the consequence; `action` is
 * `details[0].message` and says what to set, on which record. Collapsing them
 * with a `??` drops one of two things that are both true — the failure this
 * codebase has already shipped four times — so the caller renders both.
 */
export interface CompanyTaxRateRefusal {
  /** The server's own sentence. Rendered verbatim; we add nothing to it. */
  message: string;
  /**
   * The `details[0]` sentence, on `path: "companyId"`. Null when the refusal
   * carried no details array at all, which is not a shape we have seen but is
   * not worth throwing over.
   */
  action: string | null;
}

/**
 * Reads a failed `raiseInvoiceForDeal` into the refusal above, or `null`.
 *
 * ⚠️ **Matched on `error.code`, never on the message.** The house rule, and the
 * reason this returns `null` rather than a catch-all: **an unrecognised code
 * means "newer than this branch", never "not a refusal"**, so the caller must
 * fall through to the server's own message rather than to a default
 * destination. Same designed degradation as `xero.ts`'s refusal map.
 *
 * ⚠️ **The refusal has two sentences and only one of them names a company.**
 * When the deal names no company at all, the code and the `path` are identical
 * and the wording changes ("Deal 12 names no company…"). So a caller offering
 * the link must take the id from the DEAL's own `company`, and offer nothing
 * when there is none — never parse a company out of the prose, which is
 * matching on wording by another name.
 *
 * ⚠️ **Offer the company record, not a retry.** Retrying the identical request
 * can only produce the identical refusal until somebody edits the company.
 */
export function companyTaxRateRefusalOf(error: unknown): CompanyTaxRateRefusal | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code !== COMPANY_HAS_NO_TAX_RATE_CODE) return null;
  return {
    message: error.message,
    action: error.fieldErrors.companyId ?? null,
  };
}

/*
 * ⚠️ **`CONVERTED_LINES_HAVE_NO_TAX` IS DELETED, NOT RENAMED, AND THE DELETION
 * IS THE FIX.** It read: *"A deal line carries no tax rate, so every line
 * arrives with none set and 0.00 tax. Somebody has to set a rate on each
 * line."* — true of both routes until **2026-08-30**, when a converted line
 * began taking its rate from the COMPANY through the same lookup a line create
 * uses. Three surfaces rendered it (`DealDetailPage`'s invoice confirmation,
 * `InvoiceDetailPage`'s from-a-deal notice, and `QuoteFormModal` until it
 * declared a private corrected copy), so all three were making a promise about
 * a customer-facing figure that the server had stopped keeping.
 *
 * It is **deleted rather than reworded** because the two routes no longer have
 * one true sentence between them — see the pair below — so a single constant
 * could only be right about one of them. A rename would have handed the old
 * call sites a new name for a claim that had become route-dependent; an absent
 * export fails at `tsc` on every one of them instead.
 *
 * ⚠️ **Grep the SURFACE, not the name.** This paragraph mentions the old
 * constant, so a search for the string finds the note recording its removal —
 * the trap this codebase has already sprung on itself. Search for an import of
 * it, which is what `tsc` does.
 */

/**
 * What a copied line's TAX RATE does on the **quote** route.
 *
 * ⚠️ **IT HAS TO BE TRUE IN BOTH CASES AT ONCE, and that is the whole
 * difficulty.** Since 2026-08-30 a converted line takes its rate from the
 * COMPANY:
 *
 *  - the company STATES a rate → every copied line arrives carrying it;
 *  - the company states NOTHING → nothing is written, the column keeps its
 *    `0.00` default, and somebody still has to set a rate on each line.
 *
 * ⚠️ **IT MUST NOT PROMISE A FIGURE.** Not one of the 148 live companies
 * records a rate today, so the second branch is currently EVERY quote
 * conversion — a sentence reading "each line arrives with the company's rate"
 * would describe an outcome nobody can currently produce. Hence "with none
 * recorded", never "so".
 *
 * ⚠️ **That measurement is recorded HERE and deliberately NOT in the
 * sentence.** "No company records one today" is a reading with a date on it,
 * and the day somebody sets the first company rate it becomes a false claim
 * printed on screen with nothing to notice. The conditional wording is already
 * correct in both worlds, which is what makes the count unnecessary in the
 * copy.
 *
 * ⚠️ **AND IT MUST NOT LET `0.00` READ AS ZERO-RATED.** That figure is the
 * column default — nobody has said yet — not a tax treatment somebody chose.
 * The difference is the amount a customer is asked for.
 *
 * ⚠️ **It says the rate is ABSENT, never that the tax is NONE.** A line's
 * `xeroTaxType` is still `null` after either conversion, and that means
 * *inherit the company's Xero sales default*; a sentence reading "no tax"
 * would be a claim about what the customer owes.
 */
export const CONVERTED_LINE_TAX_RATE =
  'Tax rate comes from the company. With none recorded, lines arrive at 0.00 — unset, not ' +
  'zero-rated.';

/**
 * The same fact on the **invoice** route, where one half of it is unreachable.
 *
 * ⚠️ **TWO CONSTANTS, AND THE SECOND IS NOT A DUPLICATE OF THE FIRST.** The
 * conditional half of {@link CONVERTED_LINE_TAX_RATE} — "with none recorded" —
 * **cannot happen here**: `POST /deals/{id}/invoices` refuses with
 * {@link COMPANY_HAS_NO_TAX_RATE_CODE} when the deal has lines and the company
 * records no rate, and that check runs **before the transaction opens**. So an
 * invoice conversion that produced any line at all is one whose company had a
 * rate, and every line on it carries that rate.
 *
 * Rendering the quote sentence here would describe a `0.00` outcome the server
 * makes impossible on this route, which is the *reassuring* direction: somebody
 * would go looking for a rate to set on lines that already have one. The two
 * are held side by side, in one module, precisely so neither can be reworded
 * without the other being read.
 *
 * ⚠️ **The refusal is named in the sentence on purpose.** It is the first thing
 * most people will meet on this button — no live company records a rate — so a
 * sentence that only describes the success case leaves the refusal looking like
 * a fault rather than the rule it is.
 */
export const CONVERTED_INVOICE_LINE_TAX_RATE =
  'Every line arrives with the company’s tax rate. Without one recorded on the company, the ' +
  'conversion is refused rather than billing no tax.';

/*
 * ⚠️ **`CONVERSION_LEAVES_THE_DEAL_ALONE` IS DELETED (owner ruling 2026-10-18).**
 * It read "The deal itself is unchanged — its stage does not move." That stopped
 * being true for the STAGE: the server now advances the deal after the document
 * commits. Replaced by the route-specific pair below rather than reworded, so
 * every old call site fails at `tsc` instead of keeping a false sentence.
 */

/**
 * What raising or linking a **quote** does to the deal's stage.
 *
 * Since 2026-10-18 the server moves the deal to the stage flagged
 * `isQuoteSent` whenever a quote is created from it or linked to it (Draft
 * counts), and leaves a system note on the deal. It never moves backwards: a
 * deal already won, lost, or at a later stage stays where it is. Only the stage
 * moves — the deal's lines are never written back.
 *
 * A fact the reader cannot otherwise see (the move happens after the save), so
 * it is not helper text.
 */
export const QUOTE_CONVERSION_MOVES_THE_DEAL =
  'The deal moves to the quote-sent stage unless it is already further along.';

/**
 * What raising an **invoice** straight from a deal does to the deal's stage.
 *
 * Since 2026-10-18 `POST /deals/{id}/invoices` moves the deal to the won stage
 * (which stamps its recognised/closing date and sends the "Deal won"
 * notification), unless it is already won or lost. A system note records it.
 * The deal's lines are never written back.
 */
export const INVOICE_CONVERSION_MOVES_THE_DEAL =
  'The deal moves to the won stage unless it is already won or lost.';

/** What {@link invoiceBaselineFromDeal} needs that a `Deal` cannot supply. */
export interface DealInvoiceBaselineOptions {
  /**
   * The id of the `Draft` row of `/invoice-statuses`, as a string.
   *
   * The server resolves that row **by name**, so a pure module cannot do the
   * lookup; the caller passes the id it resolved from statuses it already
   * fetched. `''` leaves the picker to decide, which is an honest blank rather
   * than a default — guessing an id here would put a status on a financial
   * document nobody chose.
   */
  draftStatusId?: string;
  /**
   * Today as `YYYY-MM-DD`. Defaults to `todayIsoDate()`, which assembles it from
   * LOCAL calendar parts — `toISOString().slice(0,10)` hands back tomorrow for
   * anyone east of Greenwich in their evening. Injectable so a test can pin it.
   */
  today?: string;
  /** Defaults to `INVOICE_PAYMENT_TERM_DAYS`. */
  paymentTermDays?: number;
}

/**
 * The header values `POST /deals/{id}/invoices` is ABOUT TO WRITE, as form
 * strings — the convert BASELINE, and nothing else.
 *
 * ⚠️ **IT REPRODUCES THE SERVER'S OWN COPY, FIELD FOR FIELD — that is the whole
 * specification**, exactly as `quote-to-invoice.ts` is for the quote→invoice
 * conversion and `convertBaselineFromDeal` is for deal→quote. A baseline that
 * quietly differs from what the conversion writes does not merely look wrong —
 * **it decides what the follow-up PATCH sends**: a field seeded to something the
 * server never wrote is re-sent needlessly, and a field seeded to something the
 * server DID write is silently never corrected.
 *
 * What the server copies, and is therefore seeded here: the deal's **name** as
 * `subject`, its **company**, **contact** and **currency**, `dealId` back as
 * provenance, the **Draft** status, `billDate` = today and `dueDate` =
 * `billDate + 30`.
 *
 * ⚠️ **`ownerId`, `accountLeadId` and `projectId` are copied by the server and
 * are absent from `InvoiceHeaderPrefill`**, so they are absent here too. Each is
 * structurally unreachable in `InvoiceFormModal` — a key with no control behind
 * it goes out as `null` on an unrelated edit, and an explicit `null` CLEARS.
 * Adding one here is the first step back towards that write.
 *
 * ⚠️ **`terms`, `reference`, `xeroBrandingThemeId` and `xeroLineAmountType` stay
 * `''` because A DEAL HAS NO SUCH COLUMNS.** There is nothing to copy and the
 * conversion writes none of them, so the stored values are `null` — "use the
 * company's Xero sales default", in the last case. ⚠️ Seeding the create default
 * `'EXCLUSIVE'` into this BASELINE would show `EXCLUSIVE` over an invoice that
 * is going to store `null`, with nothing sent to close the gap. The form seeds
 * it into the VALUES instead, where it differs from the baseline and is
 * therefore actually written.
 *
 * ⚠️ **The four totals stay `''`.** The server recomputes all four from the
 * lines it has just copied, in the same transaction, and returns them — so
 * anything seeded here would be a figure the header claims and its lines do not
 * justify. It is also why the deal's own `amount` is not carried: a header
 * figure no line justifies is how a bill for the wrong amount reaches Xero.
 *
 * ⚠️ **The two DATES are a PREDICTION, not a promise.** They are built from the
 * VIEWER's local today and the server dates the invoice from its own; east of
 * Greenwich those are different days for the first hours of the morning. Because
 * they sit in the baseline as well as the values, an untouched pair is absent
 * from the diff and **the server's answer stands** — the right way round. A date
 * somebody EDITS is a genuine change and is patched, both keys together, because
 * the `billDate > dueDate` check runs only when the server sees both.
 */
export function invoiceBaselineFromDeal(
  deal: Deal,
  options: DealInvoiceBaselineOptions = {},
): InvoiceHeaderPrefill {
  const billDate = options.today ?? todayIsoDate();
  const termDays = options.paymentTermDays ?? INVOICE_PAYMENT_TERM_DAYS;

  return {
    subject: deal.dealName,
    statusId: options.draftStatusId ?? '',
    contactId: deal.contact ? String(deal.contact.id) : '',
    companyId: deal.company ? String(deal.company.id) : '',
    dealId: String(deal.id),
    billDate,
    dueDate: addDaysIso(billDate, termDays),
    /*
     * Uppercased, for the same reason every other seed in this app uppercases a
     * currency: a code stored in lower case on an older deal must not read as a
     * different currency from the same code chosen in the picker — and
     * `?currency=usd` is a 422 on an invoice write.
     */
    currency: deal.currency === null ? '' : deal.currency.toUpperCase(),
    currencyRate: '',
    subTotal: '',
    discountTotal: '',
    taxTotal: '',
    grandTotal: '',
    terms: '',
    reference: '',
    xeroLineAmountType: '',
    xeroBrandingThemeId: '',
    xeroTrackingLocation: '',
    xeroTrackingTourType: '',
  };
}
